// AI-CONTEXT: See docs/features/materiaal-uitkomsten-tegels.md
import Modal from '../common/Modal'
import formatEuro from '../../helpers/formatEuro'
import formatCo2 from '../../helpers/materialsHelpers/formatCo2'
import { interventieDocumentUrl } from '../../helpers/meetstandaard/interventieVersions'

/**
 * @typedef {import('../../helpers/materialsHelpers/buildInterventionCalculation').InterventionCalculation} InterventionCalculation
 * @typedef {import('../../helpers/materialsHelpers/buildInterventionCalculation').CalculationPart} CalculationPart
 */

/**
 * Getal in Nederlandse notatie, met zoveel decimalen als de standaard geeft
 * (0,25585 blijft 0,25585) maar hoogstens `max`.
 *
 * @param {number} value
 * @param {number} [max]
 * @returns {string}
 */
const nl = (value, max = 5) => new Intl.NumberFormat('nl-NL', { maximumFractionDigits: max }).format(value)

/** @param {number} kg */
const kg = (kg) => `${nl(kg, 2)} kg`

/**
 * Eén rekenblok: de formule letterlijk, dan de stappen ingevuld.
 *
 * @param {object} props
 * @param {string} props.title - Kop van het blok
 * @param {CalculationPart} props.part - Stappen en uitkomst
 * @param {(value: number) => string} props.format - Opmaak van een uitkomst (kg of euro)
 */
const CalculationBlock = ({ title, part, format }) => (
    <div className='material-calculation-block'>
        <span className='msi-onderbouwing-subheading'>{title}</span>
        {part.formula && <code className='material-calculation-formula'>{part.formula}</code>}
        <ul className='material-calculation-steps'>
            {part.steps.map((step) => (
                <li key={step.factorName}>
                    <span>{`${step.label}: ${nl(step.amount)} ${step.amountUnit} × ${nl(step.factor)} ${step.factorUnit} = ${format(step.result)}`}</span>
                    {step.bron && <span className='material-calculation-bron'>Bron {step.factorName}: {step.bron}</span>}
                </li>
            ))}
            {part.bestendiging !== 1 && (
                <li>{`× bestendiging ${nl(part.bestendiging)} (gedragsmaatregel)`}</li>
            )}
        </ul>
        {part.perUnit !== null && (
            <p className='material-calculation-result'>{`Per eenheid per jaar: ${format(part.perUnit)}`}</p>
        )}
    </div>
)

/**
 * Hoe de meetstandaard het getal van één maatregel uitrekent, stap voor stap,
 * met de bron van elk getal en een link naar het brondocument zelf.
 *
 * @param {object} props
 * @param {InterventionCalculation|null} props.calculation - null = venster dicht
 * @param {{Units: number, Co2Kg: number, PrivateEur: number}|null} props.totals - Wat de selectie met deze maatregel opleverde
 * @param {string} props.scopeLabel - 'alle projecten' of de projectnaam
 * @param {() => void} props.onClose - Sluit het venster
 */
const MaterialCalculationModal = ({ calculation, totals, scopeLabel, onClose }) => {
    if (!calculation) return null

    const { co2, eur } = calculation
    const documentUrl = calculation.standaard && calculation.version
        ? interventieDocumentUrl(calculation.standaard, calculation.version)
        : ''

    const content = (
        <div className='msi-onderbouwing material-calculation'>
            <CalculationBlock title='Bespaarde CO2' part={co2} format={kg} />

            {eur ? (
                <CalculationBlock title='Bespaard op energierekening' part={eur} format={formatEuro} />
            ) : (
                <p className='participation-value-note'>Deze maatregel verlaagt de energierekening niet.</p>
            )}

            {!calculation.matchesPublished && (
                <p className='msi-onderbouwing-status-notice'>
                    {`De stappen komen niet precies uit op wat de meetstandaard publiceert (${kg(co2.published ?? 0)}`}
                    {eur && eur.published !== null ? ` en ${formatEuro(eur.published)}` : ''}
                    {'). De tegel rekent met de gepubliceerde waarde.'}
                </p>
            )}

            {totals && (
                <div className='material-calculation-block'>
                    <span className='msi-onderbouwing-subheading'>{`Voor ${scopeLabel}`}</span>
                    <ul className='material-calculation-steps'>
                        <li>{`${nl(totals.Units)} × ${kg(co2.published ?? co2.perUnit ?? 0)} = ${formatCo2(totals.Co2Kg)} per jaar`}</li>
                        {eur && (
                            <li>{`${nl(totals.Units)} × ${formatEuro(eur.published ?? eur.perUnit ?? 0)} = ${formatEuro(totals.PrivateEur)} per jaar`}</li>
                        )}
                    </ul>
                    <p className='participation-value-note'>{`Aantal eenheden (${calculation.unit}) zoals geregistreerd bij de huishoudens.`}</p>
                </div>
            )}

            <div className='material-calculation-block'>
                <span className='msi-onderbouwing-subheading'>Onderbouwing</span>
                <dl className='material-calculation-facts'>
                    {calculation.onderbouwing && <><dt>Bron van het kengetal</dt><dd>{calculation.onderbouwing}</dd></>}
                    {calculation.bewijssterkte && <><dt>Bewijssterkte</dt><dd>{calculation.bewijssterkte}</dd></>}
                    {calculation.statusKengetal && <><dt>Status kengetal</dt><dd>{calculation.statusKengetal}</dd></>}
                    {calculation.rekenmodel && <><dt>Rekenmodel</dt><dd>{calculation.rekenmodel}</dd></>}
                    {calculation.afbakening && <><dt>Afbakening</dt><dd>{calculation.afbakening}</dd></>}
                </dl>
            </div>

            <p className='msi-onderbouwing-versie'>
                {`Meetstandaard ${calculation.standaard}, versie ${calculation.version}`}
                {calculation.releasedAt ? `, gepubliceerd ${calculation.releasedAt}` : ''}
                {documentUrl && (
                    <>
                        {' · '}
                        <a href={documentUrl} target='_blank' rel='noopener noreferrer'>Bekijk het brondocument</a>
                    </>
                )}
            </p>
        </div>
    )

    return (
        <Modal
            modalOpen
            setModalOpen={onClose}
            pageHeader={`Rekenwijze — ${calculation.name} (${calculation.code})`}
            description={`Zo rekent de meetstandaard per eenheid (${calculation.unit}). Elk getal komt uit het gepubliceerde document, met de bron erbij.`}
            content={content}
            hideConfirm
            cancelLabel='Sluiten'
        />
    )
}

export default MaterialCalculationModal
